export type LandingBullet = {
  title: string
  description: string
}

export type LandingFeatureBlock = {
  id: string
  eyebrow: string
  title: string
  titleMuted: string
  body: string
  bullets: LandingBullet[]
  imageSrc: string
  imageAlt: string
}

export type LandingPillar = {
  title: string
  description: string
  imageSrc: string
  imageAlt: string
  scrollTargetId: string
}

export type LandingFaqItem = {
  id: string
  question: string
  answer: string
}

export type LandingFaqCategory = {
  title: string
  items: LandingFaqItem[]
}

export const LANDING_PILLARS_SECTION = {
  eyebrow: 'WHAT IS PELGO',
  title: 'A comprehensive approach to modern workforce fluidity.',
  titleMuted:
    'Our platform replaces fragmented vendors and internal inefficiencies with an integrated, high-impact solution built for the modern enterprise.',
} as const

export const LANDING_PILLARS: LandingPillar[] = [
  {
    title: 'Intelligent Severance Administration',
    description:
      'Stop drowning in manual offboarding logistics. Automate COBRA, 401k transitions, and compliance tracking through a single enterprise dashboard that removes human error entirely.',
    imageSrc: '/assets/landing/pillar-severance-admin.webp',
    imageAlt: 'Team reviewing severance administration workflow',
    scrollTargetId: 'how-it-works',
  },
  {
    title: 'Next-Gen Career Transition',
    description:
      'Traditional outplacement is a check-the-box formality that leaves people stranded. We combine dedicated human coaching with modern AI job-hunting tools to get your alumni hired 2x faster.',
    imageSrc: '/assets/landing/pillar-outplacement.webp',
    imageAlt: 'Professional using outplacement career tools',
    scrollTargetId: 'outplacement',
  },
  {
    title: 'Severance Maximization',
    description:
      'Reduce friction and eliminate legal risk during exits. Our proprietary framework gives departing employees flexible, optimized safety net choices so they sign their exit packages faster and with full confidence.',
    imageSrc: '/assets/landing/pillar-severance-max.webp',
    imageAlt: 'Employee reviewing severance package options',
    scrollTargetId: 'severance-max',
  },
]

export const LANDING_FEATURE_BLOCKS: LandingFeatureBlock[] = [
  {
    id: 'how-it-works',
    eyebrow: 'INTELLIGENT SEVERANCE ADMINISTRATION',
    title: 'Protect your time.',
    titleMuted:
      'Stop drowning in offboarding logistics. Automate COBRA, 401k transitions, and state-by-state compliance tracking through a single enterprise platform.',
    body: 'Offboarding is an administrative minefield of manual data entry and legal risks. Pelgo takes the burden off your plate by handling the entire backend workflow flawlessly. Separation logistics should not drag down your enterprise. Automate compliance, document tracking, and employee inquiries so your HR team can focus on the people staying.',
    bullets: [
      {
        title: 'Automated, flawless, and completely compliant logistics.',
        description:
          'Offboarding means a mountain of mandatory paperwork. Pelgo automates document distribution, signature tracking, and compliance filings, turning weeks of tedious HR oversight into a few clicks.',
      },
      {
        title: 'Eliminate manual errors, mitigate legal risks.',
        description:
          'A single missed deadline or compliance oversight can trigger costly legal headaches. Our platform ensures every separation, across different states and regulations, is executed flawlessly.',
      },
      {
        title: 'Streamline the administrative burden on your HR and legal teams.',
        description:
          'Stop forcing your HR team to act as support agents for departing employees. Pelgo automatically triages baseline inquiries regarding timelines, equity, and benefit extensions.',
      },
    ],
    imageSrc: '/assets/landing/feature-severance-admin.webp',
    imageAlt: 'HR team collaborating on offboarding logistics',
  },
  {
    id: 'outplacement',
    eyebrow: 'NEXT-GEN CAREER TRANSITION SERVICES',
    title: 'Protect their future.',
    titleMuted:
      "Checking a box won't help your alumni find a job. Give your departing talent a modern platform built for today's hyper-competitive market, combining real human coaching with AI-powered job hunting.",
    body: "Leaving a company is incredibly stressful—finding a new role shouldn't be. We equip your departing team with the modern tools and personalized mentorship they actually need to land on their feet 2x faster.",
    bullets: [
      {
        title: 'Job hunting in the AI era requires a completely new playbook.',
        description:
          "Standard resume templates and static job boards don't cut it anymore. We equip your departing talent with personalized AI optimization toolkits and real-time market data to cut through the noise.",
      },
      {
        title: 'True human coaching, not just software access.',
        description:
          'Technology is only half the battle. Every individual gets a dedicated career mentor to help navigate the emotional toll of a job transition, practice interviewing, and build a targeted networking strategy.',
      },
      {
        title: 'Outcome-driven results that protect your employer brand.',
        description:
          "We don't just track portal logins; we track actual job offers. When you take genuine care of your departing team, you preserve internal morale and protect your reputation on Glassdoor.",
      },
    ],
    imageSrc: '/assets/landing/feature-outplacement.webp',
    imageAlt: 'Professional holding a business card after career coaching',
  },
  {
    id: 'severance-max',
    eyebrow: 'SEVERANCE ADMINISTRATION',
    title: 'Severance maximization.',
    titleMuted:
      'Faster signatures, zero added cost. Give your departing talent the financial flexibility they need to bridge the gap, while protecting your company from legal liability and prolonged negotiations.',
    body: 'By administering severance payouts with built-in flexibility, we offer extended safety net choices to affected employees, increasing the speed and propensity to sign their exit package.',
    bullets: [
      {
        title: 'Accelerate package signatures.',
        description:
          'Delays in signing exit packages keep financial liability hanging over your business. By presenting clear, transparent, and structured choices, we remove the anxiety that causes employees to stall.',
      },
      {
        title: 'Flexibility when they need it most.',
        description:
          'Give your former employees control over their transition. Our financial framework structures payouts to maximize their runway and financial security without costing your enterprise an extra dime.',
      },
      {
        title: 'Maintain cultural integrity and goodwill.',
        description:
          'A bitter exit turns alumni into critics and damages the productivity of the team left behind. A dignified, optimized severance experience ensures departures happen on good terms.',
      },
    ],
    imageSrc: '/assets/landing/feature-severance-max.webp',
    imageAlt: 'Professional celebrating a successful career transition',
  },
]

export const LANDING_FAQ_CATEGORIES: LandingFaqCategory[] = [
  {
    title: 'General',
    items: [
      {
        id: 'faq-general-1',
        question: 'How quickly can we implement Pelgo?',
        answer:
          'We know time is sensitive. Pelgo can be deployed in as little as 48 hours, allowing you to upload employee lists and invite users immediately following notification.',
      },
      {
        id: 'faq-general-2',
        question: 'Is Pelgo suitable for remote or global workforces?',
        answer:
          'Yes. Our platform is cloud-based and supports 10+ languages, ensuring a consistent offboarding experience regardless of location.',
      },
      {
        id: 'faq-general-3',
        question: 'How does this protect our employer brand?',
        answer:
          'By investing in the future of your departing staff, you reduce negative reviews (Glassdoor) and maintain trust with remaining employees who see their colleagues treated well.',
      },
    ],
  },
  {
    title: 'Process & engagement',
    items: [
      {
        id: 'faq-process-1',
        question: 'What happens after an employee is uploaded?',
        answer:
          'They receive a branded invitation email to access the portal, where they immediately begin their intake assessment and meet their dedicated career coach.',
      },
      {
        id: 'faq-process-2',
        question: 'How long do employees have access to the platform?',
        answer:
          'Access typically lasts for 3 to 6 months depending on your plan, or until the employee secures a new role.',
      },
      {
        id: 'faq-process-3',
        question: 'Can we track the success of the outplacement?',
        answer:
          'Absolutely. Your HR dashboard provides real-time analytics on engagement rates, skills acquired, and placement success.',
      },
    ],
  },
  {
    title: 'Pricing & fees',
    items: [
      {
        id: 'faq-pricing-1',
        question: 'Is pricing per-seat or a flat enterprise fee?',
        answer:
          'We offer a flexible per-seat model for smaller cohorts and volume licensing for large-scale restructuring.',
      },
      {
        id: 'faq-pricing-2',
        question: 'Are coaching hours included in the base price?',
        answer:
          'Yes, every license includes a set number of 1:1 coaching hours, with the option for employees to purchase more if needed.',
      },
      {
        id: 'faq-pricing-3',
        question: 'Is there a setup or implementation fee?',
        answer:
          'For standard deployments, setup is included. Custom integrations with your HRIS may incur a one-time fee.',
      },
    ],
  },
]

export const LANDING_MEDIA_SECTION = {
  eyebrow: 'IN THE MEDIA',
  titleLead: 'CEO Chieh Huang on',
  titleAccent: 'retraining workers for an AI future.',
  description:
    "Chieh Huang, Pelgo founder and CEO, joins 'Squawk Box' to discuss the AI layoff business, how his company plans to retrain workers laid off due to AI, and more.",
  embedSrc: 'https://player.cnbc.com/p/gZWlPC/cnbc_global?playertype=synd&byGuid=7000427007',
} as const

export const LANDING_SECTION_GRADIENT =
  'linear-gradient(rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0.04) 100%), linear-gradient(90deg, rgb(255, 255, 255) 0%, rgb(255, 255, 255) 100%)'
